import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { FilterChip, PageHeader } from "@/components/Molecules";
import {
  SettingsBillingPanel,
  SettingsDangerPanel,
  SettingsGeneralPanel,
  SettingsInstagramPanel,
  SettingsRestrictedState,
  SettingsShippingPanel,
  SettingsSocialsPanel,
  SettingsTeamPanel,
} from "@/components/Organisms";
import { PageTemplate } from "@/components/Templates/Page";
import { SettingsSidebarTemplate } from "@/components/Templates/SettingsSidebar";
import { SETTINGS_TAB_META } from "@/lib/settings/constants";
import type { SettingsTabId, SettingsTemplateProps } from "./Settings.template.types";
import * as styles from "./Settings.template.styles";

export type {
  SettingsBillingProps,
  SettingsDangerProps,
  SettingsGeneralProps,
  SettingsInstagramProps,
  SettingsShippingProps,
  SettingsSocialsProps,
  SettingsTabId,
  SettingsTeamProps,
  SettingsTemplateProps,
} from "./Settings.template.types";

function TabPanel({
  active,
  value,
  children,
}: {
  active: SettingsTabId;
  value: SettingsTabId;
  children: React.ReactNode;
}) {
  if (active !== value) return null;
  return <>{children}</>;
}

export function SettingsTemplate({
  hasActiveStore,
  hasAccess,
  roleKey,
  roleLabel,
  tabCountLabel,
  activeTab,
  visibleTabs,
  onTabChange,
  general,
  team,
  shipping,
  socials,
  instagram,
  billing,
  danger,
}: SettingsTemplateProps) {
  const { t } = useTranslation("settings");

  const sidebarTabs = useMemo(
    () =>
      visibleTabs.map((tab) => {
        const Icon = SETTINGS_TAB_META[tab].icon;
        return {
          id: tab,
          label: t(`tabs.${tab}`),
          icon: <Icon size={19} variant="Linear" />,
        };
      }),
    [visibleTabs, t],
  );

  const activeTabMeta = SETTINGS_TAB_META[activeTab];

  if (!hasActiveStore) {
    return (
      <PageTemplate>
        <Typography color="text.secondary">{t("noStore")}</Typography>
      </PageTemplate>
    );
  }

  const tabHeader = (
    <Box sx={styles.tabHeader}>
      <Typography component="h3" sx={styles.tabTitle}>
        {t(activeTabMeta.titleKey)}
      </Typography>
      <Typography component="p" sx={styles.tabSubtitle}>
        {t(activeTabMeta.subtitleKey)}
      </Typography>
    </Box>
  );

  const tabContent = (
    <>
      <TabPanel active={activeTab} value="general">
        <SettingsGeneralPanel {...general} />
      </TabPanel>
      <TabPanel active={activeTab} value="team">
        <SettingsTeamPanel {...team} />
      </TabPanel>
      <TabPanel active={activeTab} value="shipping">
        <SettingsShippingPanel {...shipping} />
      </TabPanel>
      <TabPanel active={activeTab} value="socials">
        <SettingsSocialsPanel {...socials} />
      </TabPanel>
      <TabPanel active={activeTab} value="instagram">
        <SettingsInstagramPanel {...instagram} />
      </TabPanel>
      <TabPanel active={activeTab} value="billing">
        <SettingsBillingPanel {...billing} />
      </TabPanel>
      <TabPanel active={activeTab} value="danger">
        <SettingsDangerPanel {...danger} />
      </TabPanel>
    </>
  );

  return (
    <PageTemplate maxWidth={1200}>
      <Box sx={styles.pageStack}>
        <Box sx={styles.desktopHeader}>
          <PageHeader title={t("title")} subtitle={t("subtitle")} />
        </Box>

        {!hasAccess ? (
          <SettingsRestrictedState roleLabel={roleLabel} />
        ) : (
          <>
            <Box sx={styles.mobileTabsRow}>
              {sidebarTabs.map((tab) => (
                <FilterChip
                  key={tab.id}
                  label={tab.label}
                  active={tab.id === activeTab}
                  size="sm"
                  onClick={() => onTabChange(tab.id)}
                />
              ))}
            </Box>

            <Box sx={styles.layoutGrid}>
              <SettingsSidebarTemplate
                tabs={sidebarTabs}
                activeTab={activeTab}
                onTabChange={onTabChange}
                roleKey={roleKey}
                roleLabel={roleLabel}
                tabCountLabel={tabCountLabel}
              />

              <Box sx={styles.contentCard}>
                <Box sx={{ display: { xs: "none", md: "block" } }}>{tabHeader}</Box>
                <Box sx={{ display: { xs: "block", md: "none" } }}>{tabHeader}</Box>
                {tabContent}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </PageTemplate>
  );
}
